/**
 * Accuplacer public landing page.
 *
 * Anyone (no login) can land here, read about the placement test, and click
 * "Register for the test" to fill out a profile. Admin then takes over and
 * issues the test credentials.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    BookOpen, PenTool, FileText, ShieldCheck, ArrowRight, GraduationCap,
} from "lucide-react";

export default function AccuplacerLanding() {
    const navigate = useNavigate();
    return (
        <div className="min-h-[80vh] bg-[#FBF5E8]" data-testid="accuplacer-landing">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#134B30]/95 via-[#0E3823] to-[#0A2818]"></div>
                <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28 text-white">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-3 flex items-center gap-2">
                        <GraduationCap className="h-3.5 w-3.5" />
                        Heritage Community College
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.1] max-w-3xl">
                        HCC English Placement Test
                    </h1>
                    <p className="mt-5 text-base lg:text-lg text-white/85 max-w-2xl">
                        A proctored placement test that measures your readiness for
                        college-level English study. Sit it in person at our campus
                        or online with a registered proctor.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Button
                            size="lg"
                            onClick={() => navigate("/accuplacer/register")}
                            className="bg-[#C79520] hover:bg-[#B17E0F] text-[#0A2818] font-semibold gap-2"
                            data-testid="accuplacer-register-btn"
                        >
                            Register for the test
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            onClick={() => navigate("/accuplacer/test")}
                            className="bg-transparent border-white/40 text-white hover:bg-white/10"
                            data-testid="accuplacer-have-credentials-btn"
                        >
                            I have my test credentials
                        </Button>
                    </div>
                </div>
            </section>

            {/* What's on the test */}
            <section className="max-w-6xl mx-auto px-6 py-16">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-3">
                    What you'll be tested on
                </div>
                <h2 className="font-display text-3xl text-[#134B30] mb-10">
                    Three sections, no time limit
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <Card className="p-6 border-[#C79520]/20 bg-white">
                        <div className="h-10 w-10 rounded-full bg-[#134B30]/10 text-[#134B30] flex items-center justify-center mb-4">
                            <BookOpen className="h-5 w-5" />
                        </div>
                        <h3 className="font-display text-xl text-[#134B30] mb-1">
                            Next-Generation Reading
                        </h3>
                        <p className="text-sm text-[#5C6B64] mb-3">
                            20 multiple-choice questions across six 300–600 word
                            passages. Measures comprehension, inference, vocabulary in
                            context, and rhetorical analysis.
                        </p>
                        <div className="text-[11px] uppercase tracking-wider text-[#C79520]">
                            ~30 minutes
                        </div>
                    </Card>
                    <Card className="p-6 border-[#C79520]/20 bg-white">
                        <div className="h-10 w-10 rounded-full bg-[#134B30]/10 text-[#134B30] flex items-center justify-center mb-4">
                            <PenTool className="h-5 w-5" />
                        </div>
                        <h3 className="font-display text-xl text-[#134B30] mb-1">
                            Next-Generation Writing
                        </h3>
                        <p className="text-sm text-[#5C6B64] mb-3">
                            20 multiple-choice questions on sentence-level
                            mechanics — grammar, agreement, pronoun case,
                            modifier placement, punctuation, and word choice.
                        </p>
                        <div className="text-[11px] uppercase tracking-wider text-[#C79520]">
                            ~25 minutes
                        </div>
                    </Card>
                    <Card className="p-6 border-[#C79520]/20 bg-white">
                        <div className="h-10 w-10 rounded-full bg-[#134B30]/10 text-[#134B30] flex items-center justify-center mb-4">
                            <FileText className="h-5 w-5" />
                        </div>
                        <h3 className="font-display text-xl text-[#134B30] mb-1">
                            WritePlacer Essay
                        </h3>
                        <p className="text-sm text-[#5C6B64] mb-3">
                            A single 300–600 word argumentative essay scored against
                            the official WritePlacer eight-point rubric: purpose,
                            organisation, development, word choice and mechanics.
                        </p>
                        <div className="text-[11px] uppercase tracking-wider text-[#C79520]">
                            ~40 minutes
                        </div>
                    </Card>
                </div>
            </section>

            {/* How it works */}
            <section className="bg-white border-y border-[#E1DAC5]">
                <div className="max-w-6xl mx-auto px-6 py-16">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-3">
                        How it works
                    </div>
                    <h2 className="font-display text-3xl text-[#134B30] mb-10">
                        Four steps from registration to results
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        {[
                            ["1", "Register", "Submit your profile through the secure form below. Takes about three minutes."],
                            ["2", "Proctor approves", "The Office of the Registrar reviews your profile and contacts you to confirm whether you will sit the test in person on campus, or online over Zoom."],
                            ["3", "Take the test", "Use the credentials you receive (in person or by email) to access the test portal and complete all three sections."],
                            ["4", "Receive results", "Your proctor reviews your responses — including AI-assisted essay scoring — and sends your placement results by email within two business days."],
                        ].map(([n, title, body]) => (
                            <div key={n} className="relative pl-6">
                                <div className="absolute left-0 top-0 h-8 w-8 rounded-full bg-[#134B30] text-white font-display text-base flex items-center justify-center -translate-x-3">
                                    {n}
                                </div>
                                <h4 className="font-display text-lg text-[#134B30] mt-1 mb-1.5">
                                    {title}
                                </h4>
                                <p className="text-sm text-[#5C6B64] leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Integrity strip */}
            <section className="max-w-6xl mx-auto px-6 py-16">
                <Card className="p-8 border-[#134B30]/15 bg-[#134B30] text-white">
                    <div className="flex items-start gap-4">
                        <ShieldCheck className="h-8 w-8 text-[#C79520] flex-shrink-0" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-2">
                                Academic integrity
                            </div>
                            <h3 className="font-display text-2xl mb-3">
                                Sit it honestly. Test the once.
                            </h3>
                            <p className="text-sm text-white/85 max-w-3xl">
                                The HCC English Test is a proctored test administered under
                                the Heritage Community College Academic Integrity Policy.
                                Tab-switching, copy-paste from external sources, and any
                                form of consultation during the test are recorded and may
                                invalidate your result. Your proctor will review your
                                responses and AI-flagged anomalies before issuing a
                                placement decision.
                            </p>
                        </div>
                    </div>
                </Card>
            </section>

            {/* CTA footer */}
            <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
                <h2 className="font-display text-3xl text-[#134B30] mb-3">
                    Ready to register?
                </h2>
                <p className="text-[#5C6B64] mb-6">
                    Fill out a brief profile and the Office of the Registrar will be in touch.
                </p>
                <Button
                    size="lg"
                    onClick={() => navigate("/accuplacer/register")}
                    className="bg-[#134B30] hover:bg-[#0F3D26] text-white gap-2"
                    data-testid="accuplacer-cta-register-bottom"
                >
                    Begin registration
                    <ArrowRight className="h-4 w-4" />
                </Button>
            </section>
        </div>
    );
}
